import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * AuthGate Component
 *
 * Enforces authentication before allowing access to protected content.
 * Redirects unauthenticated users to login page while preserving cart contents
 * and storing return URL for post-authentication navigation.
 *
 * Requirements: 2.1, 2.2, 2.3
 *
 * @param children - Protected content to render when authenticated
 * @param redirectTo - Optional custom redirect path (defaults to /login)
 */
interface AuthGateProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const AuthGate: React.FC<AuthGateProps> = ({ children, redirectTo = '/login' }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    // Store the current location so we can redirect back after login
    // This preserves the user's intended destination
    // Requirements: 2.3 - Store return URL for post-auth navigation
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  // User is authenticated, render protected content
  // Requirements: 2.2 - Cart contents are preserved during redirect (handled by CartContext localStorage)
  return <>{children}</>;
};

export default AuthGate;
